import FilterDate from '@/Enums/FilterDate.enum';
import DateFormat from '@/Enums/Format/DateFormat.enum';
import { Role } from '@/Enums/Users/Role.enum';
import type {
  IClientControlOption,
  IControlOption,
  IGroupedControlOption,
  IWhitelabelControlOption,
  IWhitelabelFilterOption,
} from '@/Interfaces/ControlOption.interface';
import { IFilterFormData } from '@/Interfaces/PdfBuilder/FilterFormData.interface';
import type { IPdfCustomReportTemplate } from '@/Interfaces/PdfBuilder/Template.interface';
import { attributionReportOptions, deliveryReportOptions, platformOptions } from '@/constants/pdfBuilder/filterOptions';
import { mapWhitelabelToControlOption } from '@/helpers/Whitelabels';
import { getShortcutItemsInclusive } from '@/helpers/datePicker/shortcuts';
import { getAdvertiserFilter, getControlOptionsValues } from '@/helpers/filters/filterMapping';
import type { DateRange } from '@mui/x-date-pickers-pro';
import moment, { type Moment } from 'moment/moment';

export const getDefaultFilters = (): IFilterFormData => {
  const rangeStart = moment().subtract(6, 'days');
  const rangeEnd = moment();

  return {
    dateRange: [rangeStart, rangeEnd],
    dateRangeFormat: FilterDate.LAST_7_DAYS,
    platforms: [],
    whitelabels: [],
    clients: [],
    advertisers: [],
    deliveryReports: [],
    attributionReports: [],
  };
};

const getNamePart = (entities: IControlOption[], entityName: string, limit: number = 7) => {
  return !entities?.length || entities?.length > limit
    ? `${entityName}: (${entities!.length})`
    : `${entityName}: ${entities.map(e => e.label)}`;
};

const generateName = (
  platforms: IControlOption[],
  deliveryReports: IControlOption[],
  attributionReports: IControlOption[],
) => {
  const platformsPart = getNamePart(platforms, 'Platform');
  const deliveryReportsPart = getNamePart(deliveryReports, 'Delivery');
  const attributionReportsPart = getNamePart(attributionReports, 'Attribution');
  return `(${moment().format(DateFormat.Iso8601WithTime)}) ${platformsPart}; ${deliveryReportsPart}; ${attributionReportsPart}`.trim();
};

export const getPdfBuilderPayload = (
  formData: IFilterFormData,
  role: Role,
  clients: IClientControlOption[] = [],
  whitelabels: IWhitelabelFilterOption[] = [],
) => {
  const { dateRange } = formData;

  const platforms = getControlOptionsValues(formData.platforms);
  const deliveryReports = getControlOptionsValues(formData.deliveryReports);
  const attributionReports = getControlOptionsValues(formData.attributionReports);
  const name = generateName(formData.platforms, formData.deliveryReports, formData.attributionReports);

  const advertiserIds = getAdvertiserFilter(
    {
      whitelabels: formData.whitelabels,
      clients: formData.clients,
      advertisers: formData.advertisers,
    },
    role,
    clients,
    whitelabels,
    'value',
  );

  return {
    filters: {
      advertiserIds,
      startDate: dateRange[0]?.format(DateFormat.Iso8601),
      endDate: dateRange[1]?.format(DateFormat.Iso8601),
    },
    name,
    platforms,
    deliveryReports,
    attributionReports,
  };
};

/**
 * Builds the payload for saving a PDF Builder template. Unlike the generate/schedule
 * payload, it persists the raw filter chain (client/whitelabel/advertiser ids) so the
 * form can be restored exactly on load. Custom start/end dates are stored only for
 * CUSTOM_RANGE; named ranges resolve to actual dates at load time.
 */
export const getPdfTemplatePayload = (formData: IFilterFormData, name: string) => {
  const { dateRange, dateRangeFormat } = formData;
  const dateParams =
    dateRangeFormat === FilterDate.CUSTOM_RANGE
      ? {
          filterStartDate: dateRange[0]?.format(DateFormat.Iso8601),
          filterEndDate: dateRange[1]?.format(DateFormat.Iso8601),
        }
      : {};

  return {
    name,
    filterDate: dateRangeFormat,
    clientIds: getControlOptionsValues(formData.clients),
    whitelabelIds: getControlOptionsValues(formData.whitelabels),
    advertiserIds: getControlOptionsValues(formData.advertisers),
    platforms: getControlOptionsValues(formData.platforms),
    deliveryReports: getControlOptionsValues(formData.deliveryReports),
    attributionReports: getControlOptionsValues(formData.attributionReports),
    ...dateParams,
  };
};

const matchOptions = <T extends IControlOption>(options: T[], values: unknown[]): T[] => {
  const valueSet = new Set(values);
  return options.filter(option => valueSet.has(option.value));
};

/** Selects entries whose value is in `ids`, preserving the first occurrence per value. */
const pickByIds = <T extends IControlOption>(pool: T[], ids: Set<unknown>): T[] => {
  const seen = new Set<unknown>();
  return pool.filter(option => {
    if (!ids.has(option.value) || seen.has(option.value)) return false;
    seen.add(option.value);
    return true;
  });
};

/**
 * Converts a hydrated template into PDF Builder form values. Platform/report enums are
 * mapped back to their control options. Client/whitelabel/advertiser ids are upgraded to
 * the full filter-source options so they carry the nested relations report generation
 * needs. Clients may live either in the flat client list (client-level users) or nested
 * inside whitelabels (whitelabel-level users), so both sources are pooled. When an id is
 * missing from the loaded pool the backend-provided label/value pair is used as fallback.
 */
export const hydratePdfTemplate = (
  template: IPdfCustomReportTemplate,
  allClients: IClientControlOption[],
  allWhitelabels: IWhitelabelControlOption[],
): Pick<
  IFilterFormData,
  | 'dateRange'
  | 'dateRangeFormat'
  | 'platforms'
  | 'deliveryReports'
  | 'attributionReports'
  | 'clients'
  | 'whitelabels'
  | 'advertisers'
> => {
  const shortcuts = getShortcutItemsInclusive();
  const { dateRangeFormat, dateRange } = template;

  const parsedDateRange: DateRange<Moment> =
    dateRangeFormat === FilterDate.CUSTOM_RANGE
      ? (dateRange.map(date => (date ? moment(date) : null)) as DateRange<Moment>)
      : (shortcuts.find(shortcut => shortcut.value === dateRangeFormat)?.getValue() as DateRange<Moment>);

  // Raw parsed whitelabels expose `id`, not the `value` control-option key the popover
  // matches against, so map them the same way the filter popover does.
  const whitelabelPool: IWhitelabelControlOption[] = allWhitelabels.map(whitelabel =>
    'value' in whitelabel ? whitelabel : mapWhitelabelToControlOption(whitelabel as never),
  );

  const clientPool: IClientControlOption[] = [
    ...allClients,
    ...whitelabelPool.flatMap(whitelabel => whitelabel.clients ?? []),
  ];
  const advertiserPool: IControlOption[] = clientPool.flatMap(client => client.advertisers ?? []);

  const clientIds = new Set(template.clients.map(client => client.value));
  const whitelabelIds = new Set(template.whitelabels.map(whitelabel => whitelabel.value));
  const advertiserIds = new Set(template.advertisers.map(advertiser => advertiser.value));

  const matchedAdvertisers = pickByIds(advertiserPool, advertiserIds);
  const matchedAdvertiserIds = new Set(matchedAdvertisers.map(advertiser => advertiser.value));
  // Fall back to backend label/value pairs for advertisers not present in the loaded pool.
  const advertisers = [
    ...matchedAdvertisers,
    ...template.advertisers.filter(advertiser => !matchedAdvertiserIds.has(advertiser.value)),
  ];

  return {
    dateRange: parsedDateRange,
    dateRangeFormat,
    platforms: matchOptions(platformOptions, template.platforms),
    deliveryReports: matchOptions(deliveryReportOptions as IGroupedControlOption[], template.deliveryReports),
    attributionReports: matchOptions(attributionReportOptions as IGroupedControlOption[], template.attributionReports),
    clients: pickByIds(clientPool, clientIds),
    whitelabels: pickByIds(whitelabelPool, whitelabelIds),
    advertisers,
  };
};
